/**
 * 15 §2.3 disc face — pure geometry for the renderer: a depth-scaled ellipse
 * (ry = 0.45 r) split into a lit and an unlit half at the floodlight azimuth,
 * an accent rim ring, the team hatch (A diagonals, B dot dashes) rotating with
 * the spin phase and clipped to the inner ellipse (0.7 r), and the 09 §2 state
 * outline: live solid / returning dashed / ground dotted. Origin at the disc
 * centre, y down (screen); the caller translates.
 */
import type { DiscStateName, Team } from '../../../sim/types.js'
import { unlitHalf, type Polyline, type Pt } from './athlete.js'

export type DiscOutline = 'solid' | 'dashed' | 'dotted'
export interface DiscFace { lit: Polyline; unlit: Polyline; rim: Polyline; hatch: Polyline[]; outline: DiscOutline }

const SQUASH = 0.45
const INNER = 0.7

function ellipse(rx: number, ry: number, n: number): Polyline {
  const out: Polyline = []
  for (let i = 0; i <= n; i++) { const a = (i / n) * Math.PI * 2; out.push({ x: Math.cos(a) * rx, y: Math.sin(a) * ry }) }
  return out
}

/** Clip the segment a→b (unit-circle space) to the unit disc; null when outside. */
function clipToUnit(a: Pt, b: Pt): [Pt, Pt] | null {
  const dx = b.x - a.x, dy = b.y - a.y
  const A = dx * dx + dy * dy, B = 2 * (a.x * dx + a.y * dy), C = a.x * a.x + a.y * a.y - 1
  if (A < 1e-12) return C <= 0 ? [a, b] : null
  const disc = B * B - 4 * A * C
  if (disc < 0) return null
  const s = Math.sqrt(disc)
  const t0 = Math.max(0, (-B - s) / (2 * A)), t1 = Math.min(1, (-B + s) / (2 * A))
  if (t1 <= t0) return null
  return [{ x: a.x + dx * t0, y: a.y + dy * t0 }, { x: a.x + dx * t1, y: a.y + dy * t1 }]
}

export function discFace(o: { r: number; spin: number; team: Team; state: DiscStateName; lightDx: number }): DiscFace {
  const r = o.r, ry = r * SQUASH
  const face = ellipse(r, ry, 24)
  const side: -1 | 1 = o.lightDx < 0 ? -1 : 1
  // One hard step: the split line sits a little toward the shade so the lit half is the larger one.
  const splitX = -side * r * 0.12
  const unlit = unlitHalf(face, splitX, side)
  const lit = unlitHalf(face, splitX, side === -1 ? 1 : -1)
  // Hatch in unit-circle space (x/ir, y/iry), rotated by the spin phase (one turn per unit), then clipped.
  const ir = r * INNER, iry = ry * INNER
  const ang = o.spin * Math.PI * 2
  const c = Math.cos(ang), s = Math.sin(ang)
  const rot = (x: number, y: number): Pt => ({ x: x * c - y * s, y: x * s + y * c })
  const toFace = (p: Pt): Pt => ({ x: p.x * ir, y: p.y * iry })
  const hatch: Polyline[] = []
  const pitch = Math.max(0.16, 3 / Math.max(6, r)) // ≈ 3 px on screen, never denser than 6 lines across the face
  if (o.team === 0) {
    // 45° diagonals ///
    for (let k = -1.4; k <= 1.4; k += pitch) {
      const a = rot(k - 2, -2 + k * 0), b = rot(k + 2, 4 + k * 0)
      const seg = clipToUnit({ x: a.x, y: a.y }, { x: b.x, y: b.y })
      if (seg) hatch.push([toFace(seg[0]), toFace(seg[1])])
    }
  } else {
    // Dot grid ∷ as short dashes
    for (let gy = -1; gy <= 1; gy += pitch) for (let gx = -1; gx <= 1; gx += pitch) {
      const a = rot(gx, gy), b = rot(gx + 0.05, gy)
      if (a.x * a.x + a.y * a.y <= 1 && b.x * b.x + b.y * b.y <= 1) hatch.push([toFace(a), toFace(b)])
    }
  }
  const outline: DiscOutline = o.state === 'RETURNING' ? 'dashed' : o.state === 'GROUND' || o.state === 'DESPAWN' ? 'dotted' : 'solid'
  return { lit, unlit, rim: face, hatch, outline }
}

/** Spin phase (turns) from render time: 6 turns per second while the disc is moving. */
export const discSpin = (renderTimeMs: number): number => ((renderTimeMs / 1000) * 6) % 1
